import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, AlertTriangle } from "lucide-react";
import { api, CATEGORIES } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";

export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [sectors, setSectors] = useState("");
  const [hasCar, setHasCar] = useState(false);
  const [cats, setCats] = useState([]);
  const [availability, setAvailability] = useState("");
  const [bio, setBio] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (user?.helper_profile) {
      setSectors((user.helper_profile.sectors || []).join(", "));
      setHasCar(!!user.helper_profile.has_car);
      setCats(user.helper_profile.categories || []);
      setAvailability(user.helper_profile.availability || "");
      setBio(user.helper_profile.bio || "");
    }
  }, [user]);

  const toggle = (id) => setCats((c) => c.includes(id) ? c.filter((x) => x !== id) : [...c, id]);

  const save = async () => {
    try {
      await api.put("/helper/profile", {
        sectors: sectors.split(",").map((s) => s.trim()).filter(Boolean),
        has_car: hasCar, categories: cats, availability, bio,
      });
      await refresh();
      toast.success("Profil mis à jour");
    } catch { toast.error("Erreur"); }
  };

  const deleteAccount = async () => {
    if (confirmText.trim().toUpperCase() !== "SUPPRIMER") {
      toast.error("Tape SUPPRIMER pour confirmer");
      return;
    }
    setDeleting(true);
    try {
      await api.delete("/auth/me");
      toast.success("Ton compte a été supprimé. Au revoir 👋");
      logout();
      nav("/", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la suppression");
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <h1 className="text-3xl md:text-4xl font-heading font-black text-navy">Mon profil</h1>

      <div className="card-surface p-6 space-y-2">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-full bg-navy text-lime font-black flex items-center justify-center text-2xl">
            {user.full_name[0]?.toUpperCase()}
          </div>
          <div>
            <div className="font-heading font-black text-xl text-navy">{user.full_name}</div>
            <div className="text-sm text-[#475569]">{user.email}</div>
            <div className="text-sm text-[#475569]">{user.phone || "—"} · {user.area || "—"}</div>
          </div>
          {user.role === "helper" && user.helper_profile?.badge && (
            <span className="chip-navy ml-auto" data-testid="profile-badge">{user.helper_profile.badge}</span>
          )}
        </div>
      </div>

      {user.role === "helper" && (
        <div className="card-surface p-6 space-y-4">
          <h2 className="font-heading font-black text-xl text-navy">Infos helper</h2>

          <div>
            <label className="text-sm font-bold text-navy">Secteurs desservis (séparés par virgules)</label>
            <input className="input-base mt-1" value={sectors} onChange={(e) => setSectors(e.target.value)} placeholder="Montréal, Laval" data-testid="input-sectors" />
          </div>

          <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="toggle-car">
            <input type="checkbox" checked={hasCar} onChange={(e) => setHasCar(e.target.checked)} className="h-5 w-5 accent-[#A3E635]" />
            <span className="font-bold text-navy">J'ai une voiture</span>
          </label>

          <div>
            <div className="text-sm font-bold text-navy mb-2">Tâches que je peux faire</div>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button key={c.id} onClick={() => toggle(c.id)} data-testid={`cat-toggle-${c.id}`}
                  className={`chip ${cats.includes(c.id) ? "bg-navy text-white" : ""}`}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-bold text-navy">Disponibilités</label>
            <input className="input-base mt-1" value={availability} onChange={(e) => setAvailability(e.target.value)} placeholder="Soirs et week-ends" data-testid="input-availability" />
          </div>

          <div>
            <label className="text-sm font-bold text-navy">Bio</label>
            <textarea rows={3} className="input-base mt-1" value={bio} onChange={(e) => setBio(e.target.value)} data-testid="input-bio" />
          </div>

          <button onClick={save} className="btn-primary" data-testid="save-profile">Enregistrer</button>
        </div>
      )}

      {/* Danger zone — self delete */}
      <div className="rounded-2xl border-2 border-red-200 bg-red-50 p-6" data-testid="danger-zone">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-red-500 text-white flex items-center justify-center shrink-0">
            <AlertTriangle size={20} />
          </div>
          <div className="flex-1">
            <h2 className="font-heading font-black text-xl text-red-900">Quitter Go Helper</h2>
            <p className="text-sm text-red-800/80 mt-1">
              Supprime définitivement ton compte et toutes tes données associées. Cette action est irréversible.
              {user.role === "helper" && " Tes filleuls et tes bonus en cours seront perdus."}
              {user.role === "client" && " Tes crédits Go Helper seront perdus."}
            </p>
            <button
              onClick={() => { setConfirmText(""); setDeleteOpen(true); }}
              className="mt-4 inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-full px-5 py-2.5 transition-colors"
              data-testid="open-delete-account"
            >
              <Trash2 size={16} /> Supprimer mon compte
            </button>
          </div>
        </div>
      </div>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle>Supprimer définitivement ton compte ?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-[#475569]">
            <p>Cette action est <strong className="text-red-700">irréversible</strong>. Tu vas perdre :</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Ton accès au compte et ton historique</li>
              {user.role === "helper" && <li>Tes badges, ton code de parrainage, tes filleuls et bonus</li>}
              {user.role === "client" && <li>Tes crédits Go Helper et tes filleuls</li>}
              <li>Tes messages et notifications</li>
            </ul>
            <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs">
              ⚠️ Si tu as des tâches <strong>en cours</strong>, termine-les d'abord — la suppression sera bloquée sinon.
            </p>
            <div>
              <label className="text-sm font-bold text-navy">Tape <span className="font-mono bg-surface px-1.5 py-0.5 rounded">SUPPRIMER</span> pour confirmer</label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="input-base mt-1"
                placeholder="SUPPRIMER"
                data-testid="confirm-delete-input"
                autoComplete="off"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => setDeleteOpen(false)} className="btn-outline" data-testid="cancel-delete">Annuler</button>
            <button
              onClick={deleteAccount}
              disabled={deleting || confirmText.trim().toUpperCase() !== "SUPPRIMER"}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-full px-5 py-2.5 inline-flex items-center gap-2"
              data-testid="confirm-delete-account"
            >
              <Trash2 size={16} /> {deleting ? "Suppression…" : "Oui, supprimer mon compte"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
